import React, { useState, useEffect, useReducer, Fragment, useRef, useCallback, useMemo, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import axiosInstance from "../../../../api/axiosInstance";
import { useHeaderSlot } from "../../../../contexts/HeaderSlotContext";
import "primeicons/primeicons.css";
import {
  LuChevronRight as ChevronRight,
  LuMenu as Menu,
  LuBell as Bell,
  LuUser as User,
  LuLogOut as LogOut,
  LuChevronDown as ChevronDown,
} from "react-icons/lu";
import { getWsBaseUrl, getWsUrl } from "../../../../utils/wsUrl";
import { resolveAssetUrl } from "../../../../utils/resolveAssetUrl";

const ROUTE_MAP = {
  "/dashboard": "Dashboard",
  "/clients": "clients",
  "/client-dashboard": "Client Dashboard",
  "/client-management": "Client Management",
  "/project-admins": "Project Admins",
  "/support-agents": "Support Agents",
  "/reports": "Reporting",
  "/settings": "Settings",
  "/profile": "Profile",
  "/sla": "SLA Management",
};

const toText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return value.content ?? value.message ?? value.name ?? value.token ?? JSON.stringify(value);
  }
  return "";
};

const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const NotificationBell = memo(() => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [superAdminId, setSuperAdminId] = useState(null);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const fetchId = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/v1/users/me`);
        if (isMounted) {
          setSuperAdminId(data.body?.actual_id);
        }
      } catch (err) {
        console.error("Error fetching user ID:", err);
      }
    };
    fetchId();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!superAdminId) return;

    let isMounted = true;
    const fetchNotifications = async () => {
      try {
        const res = await axiosInstance.get(`/api/v1/notifications/by-user/${superAdminId}`);
        if (isMounted) {
          setNotifications(res.data.body || res.data.notifications || []);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };
    fetchNotifications();

    const ws = new WebSocket(getWsUrl(superAdminId));
    wsRef.current = ws;
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "NOTIFICATION") {
          setNotifications((prev) => [
            {
              id: data.id,
              title: data.title,
              message: data.message,
              link: data.link,
              created_at: data.created_at,
              is_read: false,
            },
            ...prev,
          ]);
        }
      } catch (err) {
        console.error("WS message error:", err);
      }
    };
    ws.onclose = () => console.log("Notification WebSocket closed");
    ws.onerror = (err) => console.error("WebSocket error:", err);

    return () => {
      isMounted = false;
      if (wsRef.current) wsRef.current.close();
    };
  }, [superAdminId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    try {
      await axiosInstance.patch(`/api/v1/requests/notifications/${notificationId}/read-count`);
    } catch (err) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: false } : n))
      );
      console.error("Failed to mark as read", err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await Promise.all(
      unread.map((n) =>
        axiosInstance.patch(`/api/v1/requests/notifications/${n.id}/read-count`).catch(() => {})
      )
    );
  }, [notifications]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      if (!notification.is_read) await markAsRead(notification.id);
      if (notification.link) {
        navigate(notification.link);
        setOpen(false);
      }
    },
    [markAsRead, navigate]
  );

  const getRelativeTime = useCallback((ts) => {
    if (!ts) return "";
    try {
      return formatDistanceToNow(new Date(ts), { addSuffix: true });
    } catch {
      return "";
    }
  }, []);

  const toggleDropdown = useCallback(() => setOpen((v) => !v), []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
      >
        <Bell className="w-6 h-6 text-[#3B3D41]" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#3250FF] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-24px)] sm:w-[350px] bg-white rounded-[20px] p-4 border border-gray-100 shadow-[0_10px_35px_rgba(0,0,0,0.12)] z-50 overflow-hidden">
          <div className="flex items-center justify-between px-1 pb-3">
            <h3 className="text-[17px] font-bold text-[#1E293B]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-semibold text-[#603CFF] hover:text-[#4F33E0] transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`p-3.5 rounded-xl cursor-pointer transition-all ${
                    !n.is_read
                      ? "bg-[#EBEBFD] hover:bg-[#E0E0FB]"
                      : "bg-white border border-gray-100 hover:bg-gray-50"
                  }`}
                >
                  <p className={`text-[13px] leading-snug ${!n.is_read ? "font-semibold text-[#1E293B]" : "font-normal text-[#475569]"}`}>
                    {toText(n.message || n.title)}
                  </p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    {getRelativeTime(n.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>

          {notifications.length > 10 && (
            <div
              className="mt-3 pt-2 text-center text-xs font-semibold cursor-pointer text-[#603CFF] hover:text-[#4F33E0] transition-colors border-t border-gray-100"
              onClick={() => {
                navigate("/admin/super/notifications");
                setOpen(false);
              }}
            >
              View all {notifications.length} notifications →
            </div>
          )}
        </div>
      )}
    </div>
  );
});

NotificationBell.displayName = "NotificationBell";

const UserDropdown = memo(({ isOpen, onToggle, onLogout, navigate }) => {
  const dropdownRef = useRef(null);
  const [userName, setUserName] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.actual_name || u.name || u.full_name || "Super Admin";
    } catch {
      return "Super Admin";
    }
  });
  const [userEmail, setUserEmail] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.email || "";
    } catch {
      return "";
    }
  });
  const [avatarUrl, setAvatarUrl] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.avatar_url || u.avatar || u.profile_image || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    let isMounted = true;
    const fetchUser = async () => {
      try {
        const { data } = await axiosInstance.get("/api/v1/users/me");
        if (isMounted) {
          const body = data.body || data || {};
          if (body.actual_name || body.name || body.full_name) {
            setUserName(body.actual_name || body.name || body.full_name);
          }
          if (body.email) {
            setUserEmail(body.email);
          }
          const av = body.avatar_url || body.avatar || body.profile_pic || body.profile_image;
          if (av) {
            setAvatarUrl(av);
          }
        }
      } catch (err) {
        console.error("Error fetching user details in header:", err);
      }
    };
    fetchUser();
    return () => {
      isMounted = false;
    };
  }, []);

  const name = toText(userName);
  const email = toText(userEmail);
  const initials = useMemo(
    () =>
      name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("") || "SA",
    [name]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onToggle]);

  const handleProfileClick = useCallback(() => {
    navigate("/admin/super/profile");
    onToggle(false);
  }, [navigate, onToggle]);

  const handleToggleClick = useCallback(() => {
    onToggle(!isOpen);
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleClick}
        className="flex items-center px-2 py-1 gap-4 justify-between rounded-full bg-[#FFFFFFB2] border border-[#EBE7F8] hover:bg-white transition-colors"
      >
        <div className="flex items-center w-9 h-9 bg-[#1B1730] rounded-full justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
          {avatarUrl ? (
            <img
              src={resolveAssetUrl(avatarUrl)}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            initials || <User className="w-4 h-4" />
          )}
        </div>
        <div className="hidden sm:flex flex-col items-start leading-[1.1] min-w-0 flex-1">
          <span className="text-sm font-semibold text-[#1B1730] truncate w-full text-left">{name}</span>
          <span className="text-xs text-gray-500 truncate w-full text-left mt-0.5">Super Admin</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-800 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden">
          <div
            className="px-4 py-3 flex items-center gap-3"
            style={{ background: "#FFFFFF", borderBottom: "1px solid #DDE3EA" }}
          >
            <div className="w-8 h-8 rounded-full bg-[#1B1730] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img
                  src={resolveAssetUrl(avatarUrl)}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              ) : (
                initials
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
              {email && <p className="text-xs text-slate-700 truncate mt-0.5">{email}</p>}
            </div>
          </div>
          <div className="py-1">
            <button
              onClick={handleProfileClick}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-800 hover:bg-[#F3F5F7] transition-colors"
            >
              <User className="w-4 h-4 text-slate-600" />
              My Profile
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

UserDropdown.displayName = "UserDropdown";

const Breadcrumb = memo(({ navigate }) => {
  const location = useLocation();
  const rawPath = location.pathname;
  const basePrefix = "/admin/super";
  const relPath = rawPath.startsWith(basePrefix) ? rawPath.slice(basePrefix.length) : rawPath;

  const [, forceRender] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const onUpdate = () => forceRender();
    window.addEventListener("clientNameUpdated", onUpdate);
    window.addEventListener("adminNameUpdated", onUpdate);
    window.addEventListener("agentNameUpdated", onUpdate);
    window.addEventListener("applicationNameUpdated", onUpdate);
    window.addEventListener("domainNameUpdated", onUpdate);
    return () => {
      window.removeEventListener("clientNameUpdated", onUpdate);
      window.removeEventListener("adminNameUpdated", onUpdate);
      window.removeEventListener("agentNameUpdated", onUpdate);
      window.removeEventListener("applicationNameUpdated", onUpdate);
      window.removeEventListener("domainNameUpdated", onUpdate);
    };
  }, []);

  const getClientName = useCallback(
    (clientId) => location.state?.clientName || safeStorageGet(`client_name_${clientId}`) || null,
    [location.state]
  );

  const getAppName = useCallback(
    (appId) => location.state?.appName || safeStorageGet(`application_name_${appId}`) || null,
    [location.state]
  );

  const getDomainName = useCallback(
    (domainId) => location.state?.domainName || safeStorageGet(`domain_name_${domainId}`) || null,
    [location.state]
  );

  const getAdminName = useCallback(
    (adminId) => location.state?.adminName || safeStorageGet(`admin_name_${adminId}`) || null,
    [location.state]
  );

  const breadcrumbs = useMemo(() => {
    const list = [];
    const addCrumb = (name, path = null) => list.push({ name: toText(name), path });

    if (relPath === "" || relPath === "/" || relPath === "/dashboard") {
      addCrumb("Dashboard");
    } else if (relPath === "/clients/add-client") {
      addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
      addCrumb("Add Customer");
    } else if (relPath === "/clients/client-dashboard") {
      addCrumb("Customer Overview");
    } else if (relPath.includes("/clients/client-management/edit/")) {
      const segments = relPath.split("/").filter(Boolean);
      const clientId = segments[segments.length - 1];
      const clientName = getClientName(clientId);
      addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
      addCrumb(clientName || clientId, `${basePrefix}/clients/client-management/${clientId}`);
      addCrumb("Edit");
    } else if (relPath.includes("/clients/client-management/add-application/")) {
      const segments = relPath.split("/").filter(Boolean);
      const clientId = segments[segments.length - 1];
      const clientName = getClientName(clientId);
      addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
      addCrumb(clientName || clientId, `${basePrefix}/clients/client-management/${clientId}`);
      addCrumb("Add Application");
    } else if (relPath.includes("/clients/client-management/add-domain/")) {
      const segments = relPath.split("/").filter(Boolean);
      const clientId = segments[segments.length - 1];
      const clientName = getClientName(clientId);
      addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
      addCrumb(clientName || clientId, `${basePrefix}/clients/client-management/${clientId}`);
      addCrumb("Add Department");
    } else if (relPath.includes("/clients/client-management/add-project-admin/")) {
      const segments = relPath.split("/").filter(Boolean);
      const clientId = segments[segments.length - 1];
      const clientName = getClientName(clientId);
      addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
      addCrumb(clientName || clientId, `${basePrefix}/clients/client-management/${clientId}`);
      addCrumb("Add Project Admin");
    } else if (relPath.includes("/applications/") && relPath.includes("/edit")) {
      const segments = relPath.split("/").filter(Boolean);
      const appId = segments[segments.length - 1];
      const clientId = segments[segments.findIndex((s) => s === "clients") + 1];
      const clientName = getClientName(clientId);
      const appName = getAppName(appId);
      addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
      addCrumb(clientName || clientId, `${basePrefix}/clients/client-management/${clientId}`);
      addCrumb(appName ? `Edit ${appName}` : "Edit Application");
    } else if (relPath.includes("/domain/") && relPath.includes("/edit")) {
      const segments = relPath.split("/").filter(Boolean);
      const domainId = segments[segments.length - 1];
      const clientId = segments[segments.findIndex((s) => s === "clients") + 1];
      const clientName = getClientName(clientId);
      const domainName = getDomainName(domainId);
      addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
      addCrumb(clientName || clientId, `${basePrefix}/clients/client-management/${clientId}`);
      addCrumb(domainName ? `Edit ${domainName}` : "Edit Department");
    } else if (relPath.startsWith("/clients/client-management/")) {
      const segments = relPath.split("/").filter(Boolean);
      const isClientDetail = segments.length === 3 && segments[0] === "clients" && segments[1] === "client-management";
      if (isClientDetail) {
        const clientId = segments[2];
        const clientName = getClientName(clientId);
        addCrumb("Customer Overview", `${basePrefix}/clients/client-dashboard`);
        addCrumb(clientName || clientId);
      }
    } else if (relPath === "/project-admins/view") {
      addCrumb("Project Admin Overview");
    } else if (relPath === "/project-admins/new") {
      addCrumb("Project Admin Overview", `${basePrefix}/project-admins/view`);
      addCrumb("Add Project Admin");
    } else if (relPath.includes("/project-admins/edit/")) {
      const segments = relPath.split("/").filter(Boolean);
      const adminId = segments[segments.length - 1];
      const adminName = getAdminName(adminId);
      addCrumb("Project Admin Overview", `${basePrefix}/project-admins/view`);
      addCrumb(adminName ? `Edit ${adminName}` : "Edit Project Admin");
    } else if (relPath.startsWith("/project-admins/") && !relPath.includes("/view") && !relPath.includes("/edit") && !relPath.includes("/add-")) {
      const segments = relPath.split("/").filter(Boolean);
      const adminId = segments[segments.length - 1];
      const adminName = getAdminName(adminId) || adminId;
      addCrumb("Project Admin Overview", `${basePrefix}/project-admins/view`);
      addCrumb(adminName);
    } else if (relPath === "/support-agents/view") {
      addCrumb("Support Agent Overview");
    } else if (relPath.startsWith("/support-agents/") && !relPath.includes("/view") && !relPath.includes("/edit") && !relPath.includes("/add-")) {
      const segments = relPath.split("/").filter(Boolean);
      const agentId = segments[segments.length - 1];
      const agentName = location.state?.agentName || safeStorageGet(`agent_name_${agentId}`) || agentId;
      addCrumb("Support Agent Overview", `${basePrefix}/support-agents/view`);
      addCrumb(agentName);
    } else if (relPath === "/reports") {
      addCrumb("Reporting Overview");
    } else if (relPath === "/sla") {
      addCrumb("SLA Management");
    } else if (relPath === "/settings") {
      addCrumb("Settings");
    } else if (relPath === "/profile") {
      addCrumb("Profile");
    } else {
      const segments = relPath.split("/").filter(Boolean);
      let cumulative = "";
      segments.forEach((segment) => {
        cumulative += `/${segment}`;
        const mapped = ROUTE_MAP[cumulative] || segment.replace(/-/g, " ");
        const label = mapped.charAt(0).toUpperCase() + mapped.slice(1);
        addCrumb(label, `${basePrefix}${cumulative}`);
      });
    }
    return list;
  }, [relPath, getClientName, getAppName, getDomainName, getAdminName, location.state]);

  const handleHomeClick = useCallback(() => {
    navigate(`${basePrefix}/dashboard`);
  }, [navigate]);

  return (
    <div className="flex items-center space-x-1 text-[13px] text-gray-600 flex-wrap">
      <i
        className="pi pi-home text-[#475569] cursor-pointer hover:text-[#3250FF] transition-colors"
        onClick={handleHomeClick}
        title="Home"
      />
      {breadcrumbs.length > 0 && <ChevronRight className="h-6 w-6 text-[#3250FF] flex-shrink-0" />}
      {breadcrumbs.map((crumb, index) => (
        <Fragment key={index}>
          {index > 0 && <ChevronRight className="h-5 w-5 text-[#3250FF] flex-shrink-0" />}
          <span
            onClick={() => index < breadcrumbs.length - 1 && crumb.path && navigate(crumb.path)}
            className={`${
              index < breadcrumbs.length - 1 && crumb.path
                ? "text-[#3250FF] hover:text-[#2036CC] hover:underline cursor-pointer font-medium"
                : "text-[#0F172A] font-semibold"
            } truncate max-w-[200px] transition-colors`}
          >
            {crumb.name}
          </span>
        </Fragment>
      ))}
    </div>
  );
});

Breadcrumb.displayName = "Breadcrumb";

const SuperAdminHeader = ({ onOpenSidenav }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const { slot, titleSlot } = useHeaderSlot();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const path = useMemo(
    () => location.pathname.replace("/admin/super", ""),
    [location.pathname]
  );
  const isOverviewPage = useMemo(
    () => path === "/dashboard" || path === "" || path === "/",
    [path]
  );

  const getClientName = useCallback(
    (clientId) => location.state?.clientName || safeStorageGet(`client_name_${clientId}`) || null,
    [location.state]
  );

  const getAppName = useCallback(
    (appId) => location.state?.appName || safeStorageGet(`application_name_${appId}`) || null,
    [location.state]
  );

  const getDomainName = useCallback(
    (domainId) => location.state?.domainName || safeStorageGet(`domain_name_${domainId}`) || null,
    [location.state]
  );

  const getAdminName = useCallback(
    (adminId) => location.state?.adminName || safeStorageGet(`admin_name_${adminId}`) || null,
    [location.state]
  );

  const currentHeading = useMemo(() => {
    const segments = path.split("/").filter(Boolean);
    const effectivePath = segments[0] === "superadmin" ? "/" + segments.slice(1).join("/") : path;

    if (effectivePath === "" || effectivePath === "/" || effectivePath === "/dashboard") return "Dashboard";
    if (effectivePath === "/clients/add-client") return "Add Customer";
    if (effectivePath === "/clients/client-dashboard") return "Customers";

    if (effectivePath.startsWith("/clients/client-management/")) {
      const clientSegments = effectivePath.split("/").filter(Boolean);
      const clientId = clientSegments[clientSegments.length - 1];
      if (clientSegments.includes("edit")) {
        const clientName = getClientName(clientId);
        return clientName ? `${clientName}` : "Edit Customer";
      }
      return getClientName(clientId) || "Customer";
    }

    if (effectivePath.includes("/applications/") && effectivePath.includes("/edit")) {
      const appSegments = effectivePath.split("/").filter(Boolean);
      const appId = appSegments[appSegments.length - 1];
      const appName = getAppName(appId);
      return appName ? `${appName}` : "Edit Application";
    }

    if (effectivePath.includes("/domain/") && effectivePath.includes("/edit")) {
      const domainSegments = effectivePath.split("/").filter(Boolean);
      const domainId = domainSegments[domainSegments.length - 1];
      const domainName = getDomainName(domainId);
      return domainName ? `${domainName}` : "Edit Department";
    }

    if (effectivePath === "/project-admins/view") return "Project Admins";
    if (effectivePath === "/project-admins/new") return "Add Project Admin";
    if (effectivePath.includes("/project-admins/edit/")) {
      const adminSegments = effectivePath.split("/").filter(Boolean);
      const adminId = adminSegments[adminSegments.length - 1];
      const adminName = getAdminName(adminId);
      return adminName ? `${adminName}` : "Edit Project Admin";
    }
    if (effectivePath.startsWith("/project-admins/") && !effectivePath.includes("/view") && !effectivePath.includes("/edit") && !effectivePath.includes("/add-")) {
      const adminId = effectivePath.split("/").pop();
      return getAdminName(adminId) || "Project Admin Details";
    }

    if (effectivePath === "/support-agents/view") return "Support Agents";
    if (effectivePath.startsWith("/support-agents/") && !effectivePath.includes("/view")) {
      const agentId = effectivePath.split("/").pop();
      const agentName = location.state?.agentName || safeStorageGet(`agent_name_${agentId}`) || agentId;
      return agentName;
    }

    const mapped = ROUTE_MAP[effectivePath] || effectivePath.split("/").filter(Boolean).pop()?.replace(/-/g, " ") || "Dashboard";
    return mapped.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }, [path, getClientName, getAppName, getDomainName, getAdminName, location.state]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    // DEF-178: chat/session state lives in sessionStorage, which
    // localStorage.clear() leaves untouched — clear it so the next user in
    // this tab can't resume the previous one's session.
    try { sessionStorage.clear(); } catch { /* unavailable — non-fatal */ }
    navigate("/auth/sign-in");
  }, [navigate]);

  const handleHomeClick = useCallback(() => {
    navigate("/admin/super/dashboard");
  }, [navigate]);

  return (
    <div className="w-full">
      <div className="flex pt-5 pb-2 px-6 items-center justify-between w-full" style={{ background: 'transparent' }}>
        <div className="flex items-center min-w-0 flex-1">
          {isMobile && (
            <button onClick={onOpenSidenav} className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
          )}
          <div className="flex flex-col justify-center min-w-0">
            <div className="flex items-center gap-2">
              {!isMobile && isOverviewPage && (
                <i
                  className="pi pi-home text-gray-400 text-lg cursor-pointer transition-colors hover:text-[#3250FF]"
                  onClick={handleHomeClick}
                  title="Home"
                />
              )}
              {titleSlot ? (
                <div className="flex items-center">{titleSlot}</div>
              ) : (
                <h1 className="text-xl font-semibold text-[#0F172A] truncate capitalize max-w-[180px] sm:max-w-none">
                  {toText(currentHeading)}
                </h1>
              )}
            </div>
            {!isOverviewPage && (
              <div className="mt-0.5 hidden sm:flex">
                <Breadcrumb navigate={navigate} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3 ml-4">
          {slot && <div className="flex items-center">{slot}</div>}
          <div className="flex items-center space-x-2">
            <NotificationBell />
            <UserDropdown
              isOpen={showDropdown}
              onToggle={setShowDropdown}
              onLogout={handleLogout}
              navigate={navigate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(SuperAdminHeader);
